export type WorkspaceOnboardingStepID = "channel" | "inventory" | "listing";

export interface WorkspaceOnboardingSignals {
  canManageChannels: boolean;
  hasConnectedChannel: boolean;
  hasManualListing?: boolean;
  inventoryItemCount: number;
  listingCount: number;
}

export interface WorkspaceOnboardingStep {
  id: WorkspaceOnboardingStepID;
  title: string;
  description: string;
  complete: boolean;
}

export interface WorkspaceOnboardingProgress {
  steps: WorkspaceOnboardingStep[];
  completed: number;
  total: number;
  complete: boolean;
  nextStepID?: WorkspaceOnboardingStepID;
}

export function workspaceOnboardingProgress(
  signals: WorkspaceOnboardingSignals
): WorkspaceOnboardingProgress {
  const steps: WorkspaceOnboardingStep[] = [
    {
      id: "channel",
      title: "Choose how you sell",
      description: "Connect a channel, or record an existing listing manually from your inventory.",
      complete: signals.hasConnectedChannel || Boolean(signals.hasManualListing)
    },
    {
      id: "inventory",
      title: "Bring in your first item",
      description: signals.hasConnectedChannel
        ? "Import it from a connected channel or add it yourself."
        : "Add the stock you want Listwork to track.",
      complete: signals.inventoryItemCount > 0
    },
    {
      id: "listing",
      title: "Link your first listing",
      description: "Connect an item to where it is listed for sale.",
      complete: signals.listingCount > 0
    }
  ];
  const completed = steps.filter((step) => step.complete).length;
  // Inventory is always a valid start; manual sellers do not need a connection.
  const orderedSteps = [steps[1], steps[0], steps[2]];
  const nextStep = orderedSteps.find((step) => !step.complete && step.id !== "channel") ??
    orderedSteps.find((step) => !step.complete && signals.canManageChannels);

  return {
    steps: orderedSteps,
    completed,
    total: steps.length,
    complete: completed === steps.length,
    nextStepID: nextStep?.id
  };
}
